/**
 * SelectionAllocation component module.
 * @module investor/components/groups/AccountSelection
 *
 */
import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import history from '../../../history';
import {Button, Column, DescriptiveText, NumberInput, Row, Tile} from '../../../shared';
import IconDropdown from '../../../shared/components/IconDropdown';
import Performance from '../../../shared/components/performance/Performance';
import {connect} from 'react-redux';
import {_getPeriods} from '../../../shared/reducers/periods';

/**
 * AccountGroup component
 * @name AccountGroup
 */
const AccountGroup = props => {
  const {group, removeAccountGroup, editAccountGroup, periods, getPeriods, size, margin, session} = props;

  const [performanceValue, setPerformanceValue] = useState(group.performance.MAX); // TODO Set default time period from the Preferences page (KST-2253)
  const [selectedPeriod, setSelectedPeriod] = useState({label: 'MAX', value: 'MAX'}); // TODO Set default time period from the Preferences page (KST-2253)

  useEffect(() => getPeriods(), []);

  const actionOptions = [
    {label: 'Remove Group', action: () => removeAccountGroup(group.id), isDisabled: !!session.runAsInvestor},
    {label: 'Edit Group', action: () => editAccountGroup(group.id, group.name), isDisabled: !!session.runAsInvestor},
  ];

  const setFormattedPeriodOptions = () => {
    const timePeriods = [];
    for (const prop in group.performance) {
      if (group.performance.hasOwnProperty(prop) && periods.filter(period => period.id === prop).length !== 0) {
        timePeriods.push({
          value: prop,
          label: periods.find(period => period.id === prop).name,
          action: () => {
            setSelectedPeriod(periods.filter(period => period.id === prop).map(period => ({label: period.name, value: period.id}))[0]);
            setPerformanceValue(group.performance[prop]);
          },
          isDisabled: !!session.runAsInvestor,
        });
      }
    }
    return timePeriods;
  };

  const columnAlignItems = (columnType) => {
    if (columnType === 'groupColumn') {
      return 'left';
    }
    if (columnType === 'accountColumn') {
      const medium = size.width < 600 ? 'right' : 'center';
      return size.width <= 455 ? 'left' : medium;
    }
    if (columnType === 'performanceColumn') {
      return size.width < 600 ? 'left' : 'right';
    }
  };

  return <Tile key={group.id} title={group.name} height={'100%'} margin={margin} actionIcon={
    <IconDropdown
        options={{
          mainSection: {options: actionOptions},
          secondarySection: {label: 'Select Performance Time Period', options: setFormattedPeriodOptions()},
        }}
        currentSelection={selectedPeriod}
        icon={'threeDot'}
        iconWidth={'30px'}
        iconHeight={'30px'}/>}>
    <Row alignSubRow={'space between'} alignItems={'top'} width={'100%'} height={'100%'} wrap={true}>
      <Column alignItems={columnAlignItems('groupColumn')} width={'190px'} layout={{grow: 1}} margin={'0 0 10px 0'}>
        <NumberInput type='currency'
                     title='GROUP TOTAL'
                     placeholder='$0'
                     dataPoint={{
                       name: 'groupTotalBalance',
                       value: group.balance.amount,
                     }}
                     decimalPlaces={2}
                     readOnly={true}
                     width='120px'/>
        <Button importance='primary'
                size='md'
                label='View Group'
                disabled={false}
                action={()=>{
                  history.push(`/groups/${group.id}`);
                }}
                width='150px'/>
      </Column>
      <Column alignItems={columnAlignItems('accountColumn')} justifySubColumn={'top'} width={size.width <= 455 ? '100%' : '190px'} layout={{grow: 1}}>
        <NumberInput type='number'
                     title='ACCOUNTS'
                     placeholder='0'
                     dataPoint={{
                       name: 'groupAccounts',
                       value: group.accounts,
                     }}
                     readOnly={true}
                     width='75px'/>
      </Column>
      <Column alignItems={columnAlignItems('performanceColumn')} justifySubColumn={'top'} width={'190px'} layout={{grow: 1}}>
        <DescriptiveText width='fit-content' fontSize='14px' font={'ProximaLight'}>
          {`PERFORMANCE (${selectedPeriod.label})`}
        </DescriptiveText>
        <Performance performance={performanceValue}
                     labelPosition={'right'}
                     size={'lg'}/>
      </Column>
    </Row>
  </Tile>;
};

const mapStateToProps = state => ({
  periods: state.periods,
  session: state.session,
});

const mapDispatchToProps = (dispatch) => ({
  getPeriods: () => dispatch(_getPeriods()),
});

export default connect(mapStateToProps, mapDispatchToProps)(AccountGroup);

AccountGroup.propTypes = {
  /**
   * Session
   */
  session: PropTypes.shape({
    runAsInvestor: PropTypes.number.isRequired,
  }),
  /**
   * SizeMe size
   */
  size: PropTypes.shape({
    width: PropTypes.number,
  }),
  /**
   * Marging for tile component
   */
  margin: PropTypes.string,
  /**
   * The group of accounts
   */
  group: PropTypes.object.isRequired,
  /**
   * The function that opens "Remove Group" modal
   */
  removeAccountGroup: PropTypes.func.isRequired,
  /**
   * The function that opens "Edit Group" modal
  */
  editAccountGroup: PropTypes.func.isRequired,
  periods: PropTypes.array.isRequired,
  getPeriods: PropTypes.func.isRequired,
};
